/* oxlint-disable react-hooks/exhaustive-deps -- see the note on the effect below */
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import useWindowFocus from 'use-window-focus';
import { shallow } from 'zustand/shallow';
import { DashboardDateRange } from '@restavo/shared/enums/dashboard';
import { useTenantContext } from 'src/context/useTenantContext';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import type { SwaggerResponse } from 'src/services/apis/config';
import type { BranchScope } from 'src/services/apis/tenantRequests';
import {
  tenantDashboardAccountingReports,
  tenantDashboardInventoryReports,
  tenantDashboardIssueReports,
  tenantDashboardSalesReports,
  tenantDashboardStaffReports,
} from 'src/services/apis/tenantRequests';
import { useDashboardStore } from 'src/states/dashboard';
import { bucketForWindow, DashboardBucket, windowForPreset } from 'src/utils/dashboardRange';
import { QUERIES } from 'src/utils/react-query';

type DashboardReportFilters = {
  startDate: string | undefined;
  endDate: string;
  bucket: DashboardBucket;
};

type DashboardReportRequestFn = (scope: BranchScope, filters: DashboardReportFilters) => Promise<SwaggerResponse<any>>;

/**
 * Shared body behind every `useDashboard*Reports` hook: tenant scope, the store's date window,
 * the window-focus roll-forward, bucket selection and the query itself. The five call sites
 * differ only in their query key and request function — verified identical otherwise (see the
 * C2 report) — which is exactly what `queryKey`/`requestFn` parameterise.
 */
const useDashboardReportQuery = (queryKey: string, requestFn: DashboardReportRequestFn) => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const { startDate, endDate, dateRange, setDates } = useDashboardStore(
    (state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      dateRange: state.dateRange,
      setDates: state.setDates,
    }),
    shallow
  );

  const windowFocused = useWindowFocus();
  const { reportingTimezone } = useCurrentRestaurant() ?? {};
  const timezone = reportingTimezone || 'Asia/Ho_Chi_Minh';

  // Deliberately keyed on windowFocused alone. The effect exists to roll a stale daily
  // view forward when the operator comes back to the tab; naming startDate/dateRange
  // would also fire it whenever they pick a date, and it would immediately snap their
  // choice back to today. setDates is a zustand action and is stable.
  useEffect(() => {
    if (windowFocused && dateRange === DashboardDateRange.DAILY) {
      const fresh = windowForPreset(DashboardDateRange.DAILY, timezone);
      if (startDate?.toISOString() !== fresh.startDate?.toISOString()) {
        setDates(fresh.startDate, fresh.endDate, DashboardDateRange.DAILY);
      }
    }
  }, [windowFocused]);

  const bucket = bucketForWindow(startDate, endDate);
  // `startDate` stays undefined rather than '' — load-bearing: axios drops undefined keys
  // from the query string, while an empty string would be sent as a literal `startDate=`.
  const filters: DashboardReportFilters = {
    startDate: startDate ? startDate.toISOString() : undefined,
    endDate: endDate.toISOString(),
    bucket,
  };

  const query = useQuery(
    [queryKey, orgSlug, restaurantId, branchId, filters],
    () =>
      requestFn(
        { orgSlug: orgSlug as string, restaurantId: restaurantId as string, branchId: branchId as string },
        filters
      ),
    {
      cacheTime: 0,
      refetchOnMount: true,
      refetchInterval: 120000,
      keepPreviousData: false,
      refetchOnWindowFocus: true,
    }
  );
  return {
    query,
    bucket,
  };
};

export const useDashboardSalesReports = () =>
  useDashboardReportQuery(QUERIES.DASHBOARD_SALES_REPORTS, tenantDashboardSalesReports);

export const useDashboardAccountingReports = () =>
  useDashboardReportQuery(QUERIES.DASHBOARD_ACCOUNTING_REPORTS, tenantDashboardAccountingReports);

export const useDashboardInventoryReports = () =>
  useDashboardReportQuery(QUERIES.DASHBOARD_INVENTORY_REPORTS, tenantDashboardInventoryReports);

export const useDashboardStaffReports = () =>
  useDashboardReportQuery(QUERIES.DASHBOARD_STAFF_REPORTS, tenantDashboardStaffReports);

export const useDashboardIssueReports = () =>
  useDashboardReportQuery(QUERIES.DASHBOARD_ISSUE_REPORTS, tenantDashboardIssueReports);
